package com.rosario.juridico.marketplace.viewmodel

import com.rosario.juridico.domain.ConsultaDataModel
import com.rosario.juridico.domain.PublicarConsultaRepository
import com.rosario.juridico.domain.PublicarConsultaResult
import com.rosario.juridico.domain.AuthUseCase
import com.rosario.juridico.marketplace.model.NivelVerificacion
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch

class ConsultasFeedViewModel(
    private val publicarConsultaRepository: PublicarConsultaRepository = PublicarConsultaRepository()
) {
    private val scope = CoroutineScope(Dispatchers.Main)

    private val _feedConsultasState = MutableStateFlow<UiState<List<ConsultaDataModel>>>(UiState.Idle)
    val feedConsultasState: StateFlow<UiState<List<ConsultaDataModel>>> = _feedConsultasState

    private val _publicarConsultaState = MutableStateFlow<UiState<String>>(UiState.Idle)
    val publicarConsultaState: StateFlow<UiState<String>> = _publicarConsultaState

    private val _filtroTipoConsulta = MutableStateFlow("cliente")
    val filtroTipoConsulta: StateFlow<String> = _filtroTipoConsulta

    init {
        loadConsultasAbiertas()
    }

    fun setFiltroTipoConsulta(tipo: String) {
        if (_filtroTipoConsulta.value != tipo) {
            _filtroTipoConsulta.value = tipo
            loadConsultasAbiertas()
        }
    }

    /**
     * Carga el feed de consultas abiertas desde Firestore ordenadas por fecha reciente.
     */
    fun loadConsultasAbiertas() {
        scope.launch {
            _feedConsultasState.value = UiState.Loading
            try {
                val consultas = publicarConsultaRepository.getConsultasAbiertas(_filtroTipoConsulta.value)
                _feedConsultasState.value = UiState.Success(consultas)
            } catch (e: Exception) {
                _feedConsultasState.value = UiState.Error(
                    e.message ?: "Error al obtener las publicaciones del Marketplace.",
                )
            }
        }
    }

    /**
     * Helper de verificación de Trust & Safety: determina si el usuario cuenta con Nivel 3 Verificado.
     */
    fun isUsuarioVerificadoNivel3(): Boolean {
        val currentAbogadoNivel = AuthUseCase.instance.currentUserAbogado.value?.nivelVerificacion
        return currentAbogadoNivel == NivelVerificacion.NIVEL_3_VERIFICADO.name
    }

    /**
     * Ejecuta la publicación de una nueva consulta jurídica coincidiendo con el modelo NoSQL de producción.
     * Soporta tanto el flujo B2C (Clientes) como el B2B (Red de Colegas / Interconsultas).
     */
    fun enviarConsultaLegal(
        descripcion: String,
        telefono: String = "",
        ubicacion: String = "",
        urgencia: String = "Media",
        tipoConsulta: String? = null,
        subtipoB2B: String? = null,
        jurisdiccionDestino: String? = null,
        modalidadHonorarios: String? = null
    ) {
        scope.launch {
            _publicarConsultaState.value = UiState.Loading
            val tipoFinal = tipoConsulta ?: _filtroTipoConsulta.value

            // 🔒 TRUST & SAFETY: Sellar la publicación B2B requiriendo Nivel 3 Verificado
            if (tipoFinal == "colegiado" && !isUsuarioVerificadoNivel3()) {
                _publicarConsultaState.value = UiState.Error(
                    "Se requiere matrícula verificada (Nivel 3 Verificado) para publicar en la Red de Colegas B2B."
                )
                return@launch
            }

            val catFinal = if (tipoFinal == "colegiado") "Interconsulta B2B" else "General"

            when (val result = publicarConsultaRepository.publicarConsultaLegal(
                categoria = catFinal,
                descripcion = descripcion,
                telefono = telefono,
                ubicacion = ubicacion,
                urgencia = urgencia,
                tipoConsulta = tipoFinal,
                subtipoB2B = subtipoB2B,
                jurisdiccionDestino = jurisdiccionDestino,
                modalidadHonorarios = modalidadHonorarios
            )) {
                is PublicarConsultaResult.Success -> {
                    _publicarConsultaState.value = UiState.Success(result.consultaId)
                    loadConsultasAbiertas()
                }
                is PublicarConsultaResult.Error -> {
                    _publicarConsultaState.value = UiState.Error(result.message)
                }
            }
        }
    }

    fun resetPublicarConsultaState() {
        _publicarConsultaState.value = UiState.Idle
    }
}
